import { sentenceById } from "@repo/hindi-shared";
import type { ReactElement } from "react";
import { useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";

import { gradeFromSelfMark } from "./grading";
import type { ExerciseProps } from "./types";

/** Wrap every "।" (danda) in `text` in a sindoor span. */
function withRubricatedDanda(text: string): (string | ReactElement)[] {
  const parts = text.split("।");
  const nodes: (string | ReactElement)[] = [];
  parts.forEach((part, index) => {
    if (part !== "") nodes.push(part);
    if (index < parts.length - 1) {
      nodes.push(
        <span key={index} className="text-sindoor">
          ।
        </span>,
      );
    }
  });
  return nodes;
}

/**
 * SentenceReading — like {@link WordReading} but at sentence scale: the
 * learner reads the sentence aloud cold, then reveals IAST + gloss and
 * self-marks. The danda (।) is rubricated in sindoor, echoing manuscript
 * punctuation conventions. Unlocked in Units 5–7 once enough of the
 * inventory is taught to spell full sentences.
 */
export function SentenceReading({
  exercise,
  audio,
  onComplete,
}: ExerciseProps) {
  const sentence = sentenceById.get(exercise.itemId);
  const [revealed, setRevealed] = useState(false);

  if (sentence === undefined) return null;

  function handleReveal(): void {
    setRevealed(true);
    void audio.speak(sentence!.devanagari);
  }

  function handleMark(ok: boolean): void {
    onComplete({ grade: gradeFromSelfMark(ok), itemId: exercise.itemId });
  }

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <p className="font-dev text-sm text-ink-soft uppercase">
        <DualLabel
          variant="inline"
          en="Read, then reveal"
          hi="पढ़िए — फिर देखिए"
        />
      </p>

      <p
        lang="hi"
        className="font-dev leading-snug text-ink"
        style={{ fontSize: "clamp(40px, 6vw, 72px)" }}
      >
        {withRubricatedDanda(sentence.devanagari)}
      </p>

      {!revealed && (
        <InkButton variant="sindoor" onClick={handleReveal}>
          <DualLabel variant="inline" en="Reveal" hi="देखिए" />
        </InkButton>
      )}

      {revealed && (
        <>
          <div>
            <p className="font-latin text-2xl text-ink-soft italic">
              {sentence.iast}
            </p>
            <p className="mt-1 font-latin text-base text-ink-soft">
              {sentence.gloss}
            </p>
          </div>

          <div className="flex flex-col items-center gap-3">
            <p className="font-latin text-sm text-ink-soft">
              <span lang="en">Did you read it correctly?</span>
            </p>
            <div className="flex gap-4">
              <InkButton variant="ghost" onClick={() => handleMark(false)}>
                <DualLabel variant="inline" en="✗ Try again" hi="फिर से" />
              </InkButton>
              <InkButton variant="sindoor" onClick={() => handleMark(true)}>
                <DualLabel variant="inline" en="✓ Correct" hi="सही" />
              </InkButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
